import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { occurrenceLabelValues } from '@/features/planned/paycheckParts';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OverflowMenu } from '@/shell/OverflowMenu';
import type { OccurrenceDiffers, OccurrenceState } from '@/api/planned';
import type { PlanSliceRow, RegisterRow } from '@/api/projections';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { clampWindow, monthBounds } from './horizon';
import type { DrillDown } from './types';

function PlannedRowMenu({
  label,
  title,
  onEditThis,
  onEditFollowing,
  onEditSeries,
  onShowSeries,
}: {
  label: string;
  title: string;
  onEditThis: () => void;
  onEditFollowing: () => void;
  onEditSeries: () => void;
  onShowSeries: () => void;
}) {
  const { t } = useTranslation();
  return (
    <OverflowMenu label={label} title={title}>
      {(close) => (
        <>
          <MenuItem
            onClick={() => {
              onEditThis();
              close();
            }}
          >
            {t('planned.editThis')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onEditFollowing();
              close();
            }}
          >
            {t('planned.editFollowing')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onEditSeries();
              close();
            }}
          >
            {t('planned.editSeries')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onShowSeries();
              close();
            }}
          >
            {t('cashflow.showSeries')}
          </MenuItem>
        </>
      )}
    </OverflowMenu>
  );
}

export interface PlanTableLabels {
  envelopeNames?: Record<number, string>;
  envelopeVirtualAccounts?: Record<number, string>;
  seriesNames?: Record<number, string>;
  categories?: Record<number, string>;
}

interface PlanTableProps {
  register: RegisterRow[];
  slices: PlanSliceRow[];
  currency: string | null;
  /**
   * The forecast's operating currency: what tmm-api serves the remainder in (TMMAPP-0175). An account
   * scope's differs from `currency` on a foreign-currency account; the other scopes' are the same.
   */
  operatingCurrency: string | null;
  owner: boolean;
  labels?: PlanTableLabels;
  /** Client-side envelope filter. Absent means every group (the virtual account filter refetches instead). */
  envelopeId?: number;
  fullFrom: string;
  fullTo: string;
  drill: DrillDown | null;
  onDrill: (drill: DrillDown) => void;
  onWiden: () => void;
  onOpenOccurrence?: (seriesId: number, originalOn: string) => void;
  onShowSeries?: (seriesId: number) => void;
  onEditFollowing?: (seriesId: number, originalOn: string) => void;
  onEditSeries?: (seriesId: number) => void;
}

interface SliceView {
  sliceRef: string | null;
  envelopeId: number | null;
  envelopeName: string | null;
  virtualAccountLabel: string | null;
  amountMinor: number | null;
  categoryId: number | null;
  categoryName: string | null;
  state: OccurrenceState | null;
  seriesTargetId: number | null;
}

interface OccurrenceGroup {
  key: string;
  seriesId: number | null;
  originalOn: string | null;
  occursOn: string;
  payee: string | null;
  amountMinor: number | null;
  state: OccurrenceState | null;
  differs: OccurrenceDiffers | null;
  partiallyExecuted: boolean | null;
  remainderMinor: number | null;
  pending: boolean | null;
  seriesName: string | null;
  slices: SliceView[];
}

function groupKey(seriesId: number | null, originalOn: string | null, occursOn: string): string {
  return `${seriesId ?? 'none'}|${originalOn ?? occursOn}`;
}

function badgesOf(group: OccurrenceGroup): string[] {
  const keys: string[] = [];
  const d = group.differs;
  if (d?.date === true) keys.push('moved');
  if (d?.amount === true) keys.push('amount');
  if (d?.slices === true) keys.push('resized');
  if (group.partiallyExecuted === true) keys.push('partial');
  if (group.slices.some((s) => s.state === 'CANCELLED')) keys.push('sliceCancelled');
  if (
    group.slices.some((s) => s.virtualAccountLabel !== null && s.virtualAccountLabel.length > 0)
  ) {
    keys.push('rerouted');
  }
  if (group.slices.some((s) => s.seriesTargetId === null && s.sliceRef !== null))
    keys.push('added');
  return keys;
}

function shiftMonth(civil: string, delta: number): string {
  const year = Number(civil.slice(0, 4));
  const month = Number(civil.slice(5, 7)) - 1 + delta;
  const d = new Date(year, month, 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

export function PlanTable({
  register,
  slices,
  currency,
  operatingCurrency,
  owner,
  labels = {},
  envelopeId,
  fullFrom,
  fullTo,
  drill,
  onDrill,
  onWiden,
  onOpenOccurrence,
  onShowSeries,
  onEditFollowing,
  onEditSeries,
}: PlanTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const [seriesFilter, setSeriesFilter] = useState<number | 'all'>('all');

  const groups = useMemo(() => {
    const map = new Map<string, OccurrenceGroup>();
    const envelopeNames = labels.envelopeNames ?? {};
    const envelopeVirtualAccounts = labels.envelopeVirtualAccounts ?? {};
    const seriesNames = labels.seriesNames ?? {};
    const categories = labels.categories ?? {};

    function ensure(
      seriesId: number | null,
      originalOn: string | null,
      occursOn: string,
    ): OccurrenceGroup {
      const key = groupKey(seriesId, originalOn, occursOn);
      const existing = map.get(key);
      if (existing) return existing;
      const created: OccurrenceGroup = {
        key,
        seriesId,
        originalOn,
        occursOn,
        payee: null,
        amountMinor: null,
        state: null,
        differs: null,
        partiallyExecuted: null,
        remainderMinor: null,
        pending: null,
        seriesName: seriesId !== null ? (seriesNames[seriesId] ?? null) : null,
        slices: [],
      };
      map.set(key, created);
      return created;
    }

    if (slices.length > 0) {
      for (const row of slices) {
        const group = ensure(row.seriesId, row.originalOn, row.occursOn);
        if (row.payee !== null) group.payee = row.payee;
        if (row.state !== null) group.state = row.state;
        if (row.differsFromSeries !== null) group.differs = row.differsFromSeries;
        if (row.partiallyExecuted !== null) group.partiallyExecuted = row.partiallyExecuted;
        if (row.remainderMinor !== null) group.remainderMinor = row.remainderMinor;
        if (row.pending !== null) group.pending = row.pending;
        group.slices.push({
          sliceRef: row.sliceRef,
          envelopeId: row.envelopeId,
          envelopeName: row.envelopeId !== null ? (envelopeNames[row.envelopeId] ?? null) : null,
          virtualAccountLabel:
            row.envelopeId !== null ? (envelopeVirtualAccounts[row.envelopeId] ?? null) : null,
          amountMinor: row.amountMinor,
          categoryId: row.categoryId,
          categoryName: row.categoryId !== null ? (categories[row.categoryId] ?? null) : null,
          state: row.state,
          seriesTargetId: row.seriesTargetId,
        });
      }
    } else {
      for (const row of register) {
        if (row.kind !== 'OCCURRENCE' && row.kind !== 'SLICE') continue;
        const group = ensure(row.seriesId, row.originalOn, row.occursOn ?? row.date);
        if (row.kind === 'OCCURRENCE') {
          group.payee = row.payee;
          group.amountMinor = row.amountMinor;
          group.state = row.state;
          group.differs = row.differsFromSeries;
          group.partiallyExecuted = row.partiallyExecuted;
          group.remainderMinor = row.remainderMinor;
          group.pending = row.pending;
        } else {
          if (row.payee !== null) group.payee = row.payee;
          if (row.state !== null && group.state === null) group.state = row.state;
          if (row.differsFromSeries !== null) group.differs = row.differsFromSeries;
          if (row.partiallyExecuted !== null) group.partiallyExecuted = row.partiallyExecuted;
          if (row.remainderMinor !== null) group.remainderMinor = row.remainderMinor;
          group.slices.push({
            sliceRef: row.sliceRef,
            envelopeId: row.envelopeId,
            envelopeName: row.envelopeName,
            virtualAccountLabel:
              row.envelopeId !== null ? (envelopeVirtualAccounts[row.envelopeId] ?? null) : null,
            amountMinor: row.amountMinor,
            categoryId: row.categoryId,
            categoryName: row.categoryId !== null ? (categories[row.categoryId] ?? null) : null,
            state: row.state,
            seriesTargetId: row.seriesTargetId,
          });
        }
      }
    }
    return [...map.values()];
  }, [register, slices, labels]);

  const seriesOptions = useMemo(() => {
    const ids = new Map<number, string>();
    for (const g of groups) {
      if (g.seriesId !== null) ids.set(g.seriesId, g.seriesName ?? String(g.seriesId));
    }
    return [...ids.entries()];
  }, [groups]);

  const filtered = groups.filter((g) => {
    if (seriesFilter !== 'all' && g.seriesId !== seriesFilter) return false;
    if (
      envelopeId != null &&
      envelopeId > 0 &&
      !g.slices.some((slice) => slice.envelopeId === envelopeId)
    ) {
      return false;
    }
    return true;
  });

  const hasSeries = owner && filtered.some((g) => g.seriesId !== null);
  const hasPayee = filtered.some((g) => {
    if (!owner) {
      const resolved = g.state !== null && g.state !== 'PLANNED' && g.state !== 'CANCELLED';
      return resolved && g.payee !== null;
    }
    return g.payee !== null;
  });
  const showRemainder = owner;
  const showActions = owner;
  const showCategory = md;
  const showException = md;
  const showSeriesCol = lg && hasSeries;
  const showOccAmount = lg && owner;

  const cursor = drill?.from ?? fullFrom;
  const cursorMonth = `${cursor.slice(0, 7)}-01`;
  const prevMonth = shiftMonth(cursorMonth, -1);
  const nextMonth = shiftMonth(cursorMonth, 1);
  const prevBounds = clampWindow(monthBounds(prevMonth), { from: fullFrom, to: fullTo });
  const nextBounds = clampWindow(monthBounds(nextMonth), { from: fullFrom, to: fullTo });
  const canPrev = prevMonth.slice(0, 7) >= fullFrom.slice(0, 7);
  const canNext = nextMonth.slice(0, 7) <= fullTo.slice(0, 7);

  const planned = (state: OccurrenceState | null) => state === 'PLANNED';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ gap: 1.5, mb: 1.5, alignItems: { md: 'center' }, flexWrap: 'wrap' }}
      >
        {owner && seriesOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="cashflow-series">{t('cashflow.filterSeries')}</InputLabel>
            <Select
              labelId="cashflow-series"
              label={t('cashflow.filterSeries')}
              value={seriesFilter === 'all' ? 'all' : String(seriesFilter)}
              onChange={(event) => {
                const v = event.target.value;
                setSeriesFilter(v === 'all' ? 'all' : Number(v));
              }}
            >
              <MenuItem value="all">{t('cashflow.filterAllSeries')}</MenuItem>
              {seriesOptions.map(([id, name]) => (
                <MenuItem key={id} value={String(id)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            disabled={!canPrev}
            onClick={() => onDrill(prevBounds)}
            aria-label={t('cashflow.monthPrev')}
          >
            {t('cashflow.monthPrev')}
          </Button>
          <Typography variant="body2">
            {formatCivilDate(cursorMonth, locale, { month: 'long', year: 'numeric' })}
          </Typography>
          <Button
            size="small"
            disabled={!canNext}
            onClick={() => onDrill(nextBounds)}
            aria-label={t('cashflow.monthNext')}
          >
            {t('cashflow.monthNext')}
          </Button>
        </Stack>
        {drill !== null && (
          <Button size="small" onClick={onWiden}>
            {t('cashflow.widen')}
          </Button>
        )}
      </Stack>

      <Table
        size="small"
        aria-label={t('cashflow.table')}
        sx={{
          // Default 16px cell padding plus the actions kebab overflows a 375px page (X4).
          [theme.breakpoints.down('md')]: {
            '& .MuiTableCell-root': { px: 1 },
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('cashflow.colDate')}</TableCell>
            {showSeriesCol && <TableCell>{t('cashflow.colSeries')}</TableCell>}
            {hasPayee && lg && <TableCell>{t('cashflow.colPayee')}</TableCell>}
            <TableCell>{t('cashflow.colEnvelope')}</TableCell>
            {showOccAmount && (
              <TableCell align="right">{t('cashflow.colOccurrenceAmount')}</TableCell>
            )}
            <TableCell align="right">{t('cashflow.colAmount')}</TableCell>
            {showCategory && <TableCell>{t('cashflow.colCategory')}</TableCell>}
            <TableCell>{t('cashflow.colState')}</TableCell>
            {showException && <TableCell>{t('cashflow.colException')}</TableCell>}
            {showActions && <ActionsHeaderCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((group) => {
            const moved = group.originalOn !== null && group.originalOn !== group.occursOn;
            const badgeKeys = badgesOf(group);
            const showPayee =
              hasPayee &&
              lg &&
              (owner ||
                (group.state !== null && group.state !== 'PLANNED' && group.state !== 'CANCELLED'));
            const eventName =
              group.payee ?? group.seriesName ?? formatCivilDate(group.occursOn, locale);
            const seriesId = group.seriesId;
            const originalOn = group.originalOn;
            const rowMenu = (name: string) =>
              seriesId === null ? null : (
                <PlannedRowMenu
                  label={t('cashflow.actionsFor', { name })}
                  title={name}
                  onEditThis={() => {
                    if (originalOn !== null) onOpenOccurrence?.(seriesId, originalOn);
                  }}
                  onEditFollowing={() => {
                    if (originalOn !== null) onEditFollowing?.(seriesId, originalOn);
                  }}
                  onEditSeries={() => onEditSeries?.(seriesId)}
                  onShowSeries={() => onShowSeries?.(seriesId)}
                />
              );
            const header = (
              // The group head sits on `surface2` (like a table head): `text`, `text2` and money
              // are guaranteed on it, where the `hover` layer guarantees only `text`
              // (TMMAPP-0049). Its exception badges paint `surface`, so their label is
              // `text / surface`; the state chip paints its tone.
              <TableRow key={`${group.key}-h`} sx={{ bgcolor: tokens.surface2 }}>
                <TableCell>
                  <Typography variant="body2">
                    {formatCivilDate(group.occursOn, locale)}
                    {moved && group.originalOn !== null && (
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                          marginInlineStart: 1,
                        }}
                      >
                        {formatCivilDate(group.originalOn, locale)}
                      </Typography>
                    )}
                  </Typography>
                </TableCell>
                {showSeriesCol && <TableCell>{group.seriesName ?? '—'}</TableCell>}
                {hasPayee && lg && <TableCell>{showPayee ? (group.payee ?? '—') : ''}</TableCell>}
                <TableCell />
                {showOccAmount && (
                  <TableCell align="right">
                    <MoneyText minor={group.amountMinor} currency={currency} tone="register" />
                  </TableCell>
                )}
                <TableCell />
                {showCategory && <TableCell />}
                <TableCell>
                  {group.state !== null && (
                    <StatusChip
                      domain="occurrence"
                      status={group.state}
                      values={occurrenceLabelValues(group.amountMinor)}
                    />
                  )}
                </TableCell>
                {showException && (
                  <TableCell>
                    <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                      {badgeKeys.map((key) => (
                        <Chip
                          key={key}
                          size="small"
                          label={t(`cashflow.badge.${key}`)}
                          sx={{ bgcolor: 'background.paper' }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                )}
                {showActions && (
                  <TableCell>
                    {planned(group.state) && group.seriesId !== null && rowMenu(eventName)}
                  </TableCell>
                )}
              </TableRow>
            );
            const sliceRows = group.slices.map((slice, index) => (
              <TableRow key={`${group.key}-${slice.sliceRef ?? index}`}>
                <TableCell />
                {showSeriesCol && <TableCell />}
                {hasPayee && lg && <TableCell />}
                <TableCell>
                  {slice.envelopeName ?? '—'}
                  {owner && slice.virtualAccountLabel !== null && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary' }}
                    >
                      {slice.virtualAccountLabel}
                    </Typography>
                  )}
                </TableCell>
                {showOccAmount && <TableCell />}
                <TableCell align="right">
                  <MoneyText minor={slice.amountMinor} currency={currency} tone="register" />
                </TableCell>
                {showCategory && <TableCell>{slice.categoryName ?? '—'}</TableCell>}
                <TableCell>
                  {slice.state !== null && (
                    <StatusChip
                      domain="occurrence"
                      status={slice.state}
                      values={occurrenceLabelValues(slice.amountMinor)}
                    />
                  )}
                </TableCell>
                {showException && <TableCell />}
                {showActions && (
                  <TableCell>
                    {planned(slice.state ?? group.state) &&
                      group.seriesId !== null &&
                      rowMenu(slice.envelopeName ?? eventName)}
                  </TableCell>
                )}
              </TableRow>
            ));
            const remainder =
              showRemainder && group.remainderMinor !== null ? (
                <TableRow key={`${group.key}-r`}>
                  <TableCell />
                  {showSeriesCol && <TableCell />}
                  {hasPayee && lg && <TableCell />}
                  <TableCell>{t('cashflow.remainder')}</TableCell>
                  {showOccAmount && <TableCell />}
                  <TableCell align="right">
                    <MoneyText minor={group.remainderMinor} currency={operatingCurrency} />
                  </TableCell>
                  {showCategory && <TableCell />}
                  <TableCell />
                  {showException && <TableCell />}
                  {showActions && <TableCell />}
                </TableRow>
              ) : null;
            return [header, ...sliceRows, remainder];
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
